import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';

/**
 * Reusable player picker with search, avatars, and ELO display.
 */
export default function PlayerPicker({ guildId, selected, onChange, maxPlayers, excludeDiscordIds, label }) {
  const { t } = useTranslation();
  const [players, setPlayers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!guildId) return;
    setLoading(true);
    fetch(`${API_URL}/api/web/players/${guildId}`)
      .then((r) => r.ok ? r.json() : [])
      .then(setPlayers)
      .catch(() => setPlayers([]))
      .finally(() => setLoading(false));
  }, [guildId]);

  const excludeSet = useMemo(
    () => new Set(excludeDiscordIds || []),
    [excludeDiscordIds]
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return players
      .filter((p) => !excludeSet.has(p.discord_id))
      .filter((p) => {
        if (!q) return true;
        return (
          (p.steam_username || '').toLowerCase().includes(q) ||
          (p.discord_username || '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => (b.elo?.elo || 0) - (a.elo?.elo || 0));
  }, [players, search, excludeSet]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const atMax = maxPlayers && selected.length >= maxPlayers;

  const toggle = (discordId) => {
    if (selectedSet.has(discordId)) {
      onChange(selected.filter((id) => id !== discordId));
    } else if (!atMax) {
      onChange([...selected, discordId]);
    }
  };

  const getPlayer = (discordId) => players.find((p) => p.discord_id === discordId);

  if (loading) {
    return <div style={{ color: '#888', fontSize: '0.9em', padding: 8 }}>{t('profile.loadingPlayers')}</div>;
  }

  return (
    <div>
      {label && (
        <div style={{ marginBottom: 8, color: '#ccc', fontWeight: 'bold', fontSize: '0.9em' }}>
          {label} ({selected.length}{maxPlayers ? `/${maxPlayers}` : ''})
        </div>
      )}

      {/* Selected players */}
      {selected.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          {selected.map((id) => {
            const p = getPlayer(id);
            return (
              <div
                key={id}
                onClick={() => toggle(id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '4px 10px', background: '#2e7d32', borderRadius: 16,
                  cursor: 'pointer', fontSize: '0.85em',
                }}
              >
                {p?.avatar && (
                  <img src={p.avatar} alt="" style={{ width: 20, height: 20, borderRadius: '50%' }} />
                )}
                <span>{p?.steam_username || p?.discord_username || id}</span>
                <span style={{ color: '#aaa', marginLeft: 2 }}>&times;</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Search */}
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t('players.searchPlaceholder')}
        style={{
          width: '100%', padding: '8px 12px', background: '#2a2a2a',
          color: '#fff', border: '1px solid #444', borderRadius: 6,
          marginBottom: 8, fontSize: '0.9em', boxSizing: 'border-box',
        }}
      />

      {/* Player list */}
      <div style={{
        maxHeight: 260, overflowY: 'auto', display: 'flex',
        flexDirection: 'column', gap: 2, borderRadius: 6,
        border: '1px solid #333', background: '#1a1a1a',
      }}>
        {filtered.length === 0 ? (
          <div style={{ padding: 16, color: '#666', textAlign: 'center', fontSize: '0.9em' }}>
            {search ? t('tournament.noPlayersFound') : t('tournament.noPlayersAvailable')}
          </div>
        ) : filtered.map((p) => {
          const isSelected = selectedSet.has(p.discord_id);
          const disabled = atMax && !isSelected;
          return (
            <div
              key={p.discord_id}
              onClick={() => !disabled && toggle(p.discord_id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 12px',
                background: isSelected ? '#1b3a1b' : 'transparent',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.4 : 1,
                borderBottom: '1px solid #222',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => { if (!disabled && !isSelected) e.currentTarget.style.background = '#252525'; }}
              onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{ position: 'relative', width: 32, height: 32 }}>
                {p.avatar ? (
                  <img src={p.avatar} alt="" style={{ width: 32, height: 32, borderRadius: '50%' }} />
                ) : (
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#333' }} />
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#eee', fontSize: '0.9em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.steam_username || p.discord_username}
                </div>
                {p.elo && (
                  <div style={{ color: '#888', fontSize: '0.75em' }}>
                    {p.elo.rank} — {p.elo.elo} ELO
                  </div>
                )}
              </div>
              <div style={{ width: 20, textAlign: 'center', color: '#4CAF50', fontWeight: 'bold' }}>
                {isSelected ? '✓' : ''}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
